/**
 * A sent form's flow, one hook for the Mini App's FormRequestPage and the website's form page
 * (shared-client-screens D3, rule 22): read the form by its link (`POST /form-request/:token/
 * bootstrap`; the token is the authority, no sign-in), keep the answers as the client types
 * (useFormRequestDraft, web-account-pages D6), submit them, and land on the state the answer means.
 *
 * States: loading → form ⇄ submitting → success; loading → consumed, revoked, unavailable or
 * error; form → submitting → form with the server's field errors (400 VALIDATION_FAILED).
 *
 * The transport is the caller's (row F8): `fetcher` takes a path under the API's `/api/webapp`
 * and a request, and returns the answer or throws its error as an `ApiErrorLike` (status, code,
 * details); a throw with no status is a network failure. What a submit refreshes is the caller's
 * too (`onSubmitted`, row F6).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  formOfBootstrap,
  terminalStateFor,
  type ApiErrorLike,
  type BootstrapResponse,
  type PageState,
} from '@/pages/FormRequestPage/formRequestParts';
import type { FieldError, FormDefinition, FormResponseInput } from '@/types/forms';
import { draftOutcomeOf, useFormRequestDraft, type DraftAnswers, type DraftSend } from './useFormRequestDraft';

export type FormRequestFetcher = <T>(path: string, init?: RequestInit) => Promise<T>;

export interface FormRequestFlowOptions {
  readonly requestToken: string | undefined;
  readonly fetcher: FormRequestFetcher;
  /** The form was sent: the caller refreshes what showed it as waiting. */
  readonly onSubmitted?: (form: FormDefinition) => void;
}

export interface FormRequestFlow {
  readonly status: PageState;
  readonly form: FormDefinition | null;
  readonly isAnonymous: boolean;
  readonly serverErrors: FieldError[];
  /** The last send failed without ending the request: the form stays with its answers, and says so. */
  readonly submitFailed: boolean;
  readonly submit: (responses: readonly FormResponseInput[]) => void;
  /** Every change on the page, the whole answer set. */
  readonly answersChanged: (answers: DraftAnswers) => void;
}

const asError = (err: unknown): ApiErrorLike => (err && typeof err === 'object' ? (err as ApiErrorLike) : {});

export function useFormRequestFlow({ requestToken, fetcher, onSubmitted }: FormRequestFlowOptions): FormRequestFlow {
  const [status, setStatus] = useState<PageState>(requestToken ? 'loading' : 'unavailable');
  const [form, setForm] = useState<FormDefinition | null>(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldError[]>([]);
  /** The last submit failed without ending the request: the form stays, with its answers. */
  const [submitFailed, setSubmitFailed] = useState(false);
  const [revision, setRevision] = useState<number | null>(null);

  const path = requestToken ? `/form-request/${encodeURIComponent(requestToken)}` : null;

  useEffect(() => {
    if (!path) {
      setStatus('unavailable');
      return undefined;
    }
    let cancelled = false;
    setStatus('loading');
    fetcher<BootstrapResponse>(`${path}/bootstrap`, { method: 'POST' }).then(
      (data) => {
        if (cancelled) return;
        setForm(formOfBootstrap(data));
        setIsAnonymous(Boolean(data.isAnonymous));
        setRevision(data.draft?.revision ?? 0);
        setStatus('form');
      },
      (err: unknown) => {
        if (!cancelled) setStatus(terminalStateFor(asError(err)));
      },
    );
    return () => {
      cancelled = true;
    };
    // The fetcher is the caller's transport, fixed for the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // web-account-pages D6: the answers are kept as the client types.
  const send = useCallback<DraftSend>(
    (init) =>
      fetcher<{ revision: number }>(`${path}/draft`, init).then(
        (res) => ({ kind: 'saved', revision: res.revision }),
        (err: unknown) => {
          const e = asError(err);
          return typeof e.status === 'number' ? draftOutcomeOf(e.status, e.code, e.details ?? {}) : { kind: 'failed' };
        },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path],
  );
  const draft = useFormRequestDraft({
    revision,
    send,
    // The request closed while the page was open: its end screen, unless a submit is under way.
    onClosed: (code) => setStatus((now) => (now === 'form' ? terminalStateFor({ code }) : now)),
  });

  const submit = useCallback(
    (responses: readonly FormResponseInput[]) => {
      if (!path) return;
      setStatus('submitting');
      setServerErrors([]);
      setSubmitFailed(false);
      draft.hold();
      fetcher(`${path}/submit`, { method: 'POST', body: JSON.stringify({ responses }) }).then(
        () => {
          setStatus('success');
          if (form) onSubmitted?.(form);
        },
        (err: unknown) => {
          const e = asError(err);
          const fieldErrors = e.details?.fieldErrors;
          if (e.status === 400 && Array.isArray(fieldErrors)) {
            setServerErrors(fieldErrors as FieldError[]);
            setStatus('form');
            draft.release();
            return;
          }
          // A request that ended (revoked, sent, gone) shows its end; anything else keeps the form, its
          // answers and their saving, and says the send failed (Codex review 2026-10-26).
          const end = terminalStateFor(e);
          if (end !== 'error') {
            setStatus(end);
            return;
          }
          setSubmitFailed(true);
          setStatus('form');
          draft.release();
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path, draft, form, onSubmitted],
  );

  return { status, form, isAnonymous, serverErrors, submitFailed, submit, answersChanged: draft.change };
}
